import React, { useState, useRef, useEffect } from 'react';
import './PhotoUploadModal.css';
import Modal from 'react-modal';

interface PhotoUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUpload: (file: File) => Promise<void>;
    currentPhotoUrl: string;
}

const PhotoUploadModal: React.FC<PhotoUploadModalProps> = ({
    isOpen,
    onClose,
    onUpload,
    currentPhotoUrl,
}) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string>(currentPhotoUrl);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Update preview when currentPhotoUrl changes
    useEffect(() => {
        setPreviewUrl(currentPhotoUrl);
    }, [currentPhotoUrl]);

    if (!isOpen) return null;

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!['image/jpeg', 'image/png'].includes(file.type)) {
            setError('Please select a JPG or PNG image file');
            return;
        }

        // Validate file size (1MB)
        if (file.size > 1 * 1024 * 1024) {
            setError('File size must be less than 1MB');
            return;
        }

        setError('');
        setSelectedFile(file);

        // Create preview URL
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(file);
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setError('Please select a file first');
            return;
        }

        try {
            setIsUploading(true);
            await onUpload(selectedFile);
        } catch (error) {
            setError('Failed to upload photo. Please try again.');
        } finally {
            setIsUploading(false);
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        setPreviewUrl(currentPhotoUrl);
        setError('');
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={handleClose}
            contentLabel="Upload Profile Photo"
            className="photo-upload-modal"
            overlayClassName="photo-upload-overlay"
        >
            <div className="photo-upload-content">
                <h2>Update Profile Photo</h2>
                {error && <p className="error-message">{error}</p>}
                <div className="current-photo">
                    <img src={previewUrl} alt="Profile preview" />
                </div>
                <div className="upload-section">
                    <input
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={handleFileSelect}
                        id="photo-upload"
                        style={{ display: 'none' }}
                    />
                    <label htmlFor="photo-upload" className="upload-button">
                        Choose File
                    </label>
                    {selectedFile && (
                        <p className="selected-file">{selectedFile.name}</p>
                    )}
                </div>
                <div className="modal-buttons">
                    <button onClick={handleClose} className="cancel-button">
                        Cancel
                    </button>
                    <button
                        onClick={handleUpload}
                        className="upload-button"
                        disabled={!selectedFile || isUploading}
                    >
                        {isUploading ? 'Uploading...' : 'Upload'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default PhotoUploadModal; 